import { withJsonFormsControlProps } from "@jsonforms/react";
import CloseIcon from "@mui/icons-material/Close";
import { Chip } from "@mui/material";
import { TextInput } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";

const EmailChipsComponent = (props) => {
  const { handleChange, label, data, path, required, enabled, visible } = props;
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");

  // Parse comma-separated string to array - wrapped in useMemo to prevent dependency changes on every render
  const emails = useMemo(
    () => (data ? data.split(",").filter((e) => e.trim()) : []),
    [data]
  );

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const updateEmails = useCallback(
    (newEmails) => {
      const emailString = newEmails.join(",");
      handleChange(path, emailString);
    },
    [handleChange, path]
  );

  const handleAddEmail = useCallback(() => {
    const trimmedEmail = inputValue.trim();
    if (!trimmedEmail) {
      setError("Please enter an email address");
      return;
    }
    if (!validateEmail(trimmedEmail)) {
      setError("Please enter a valid email address");
      return;
    }
    if (emails.includes(trimmedEmail)) {
      setError("This email is already added");
      return;
    }
    setError("");
    const newEmails = [...emails, trimmedEmail];
    updateEmails(newEmails);
    setInputValue("");
  }, [inputValue, emails, updateEmails]);

  const handleRemoveEmail = useCallback(
    (emailToRemove) => {
      const newEmails = emails.filter((email) => email !== emailToRemove);
      updateEmails(newEmails);
    },
    [emails, updateEmails]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        handleAddEmail();
      }
    },
    [handleAddEmail]
  );

  if (!visible) return null;

  return (
    <div>
      <div>
        <TextInput
          placeholderText={inputValue ? "" : props.uischema?.placeholder}
          value={inputValue}
          onUpdate={(e) => {
            setInputValue(e);
            setError("");
          }}
          onKeyDown={handleKeyDown}
          disabled={!enabled}
          error={error}
          helperText={error}
          sx={{ flex: 1 }}
          label={label}
          required={required}
        />
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}
      >
        {emails.map((email, index) => (
          <Chip
            key={`${email}-${index}`}
            label={email}
            onDelete={enabled ? () => handleRemoveEmail(email) : undefined}
            deleteIcon={<CloseIcon fontSize="small" />}
            size="small"
            sx={{
              backgroundColor: "#e3f2fd",
              "& .MuiChip-deleteIcon": { color: "#1976d2" },
            }}
          />
        ))}
      </div>
    </div>
  );
};

export const EmailChipsControl = withJsonFormsControlProps(EmailChipsComponent);

EmailChipsComponent.propTypes = {
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  data: PropTypes.string,
  path: PropTypes.string,
  required: PropTypes.bool,
  enabled: PropTypes.bool,
  visible: PropTypes.bool,
};

EmailChipsComponent.defaultProps = {
  data: "",
  required: false,
  enabled: true,
  visible: true,
};

export default EmailChipsComponent;
